import { isEmpty, isNil, isNull } from 'lodash';
// Import UI Components
import { Icon, Popover, SkeletonLoader } from '@geneui/components';
// Import Components
import UsedInSection from 'components/common/UsedInSection';
import RecurrenceList from 'components/common/RecurrenceList';
import SegmentClientsCount from 'components/common/SegmentClientsCount';
// Import Constants
import { l, ReportTypesLabels } from 'constants/common';
import { ReportContainerConfig } from 'components/CompositeReport/config';
// Import Services
import { Helpers } from 'services';
// Import Enums
import { SegmentClientsCountRequestEnum } from 'enums';

const { customMomentWithoutTimezoneConversion } = Helpers;

const DefaultDatesDict = {
    dailyView: {
        day: '',
        days: [],
        isActivePicker: false,
        month: '',
        months: [],
        viewType: 'dailyView',
        weeks: [],
        year: '',
        years: [],
    },
};

const getOperation = (operation) => ReportContainerConfig.radioGroupOptions.find((item) => item.value === operation);

const generalDetailsGroupData = (t, dateFormats) => {
    const { partnerDateFormat, partnerDateTimeWithoutSecondsFormat } = dateFormats;

    return [
        {
            title: l.GeneralDetails,
            keyValueItemsData: {
                reportId: {
                    label: t(l.ReportId),
                    isLoading: true,
                },
                name: {
                    label: t(l.Title),
                    isLoading: true,
                },
                description: {
                    label: t(l.Description),
                    isLoading: true,
                },
                executionCount: {
                    label: t(l.ExecutionCount),
                    isLoading: true,
                },
                numberOfCustomers: {
                    label: t(l.NumberOfCustomers),
                    isLoading: true,
                    renderer: ({ filtersConfig }) => {
                        return (
                            <div className="ellipsis-text kv-value kv-value-css crm-segment-filters-header-blocks crm-segment-filters-header-left-side">
                                <SegmentClientsCount
                                    isDisabled={isNil(filtersConfig)}
                                    config={{
                                        type: SegmentClientsCountRequestEnum.Filter,
                                        filtersConfig,
                                    }}
                                />
                            </div>
                        );
                    },
                },
                unifiedCurrency: {
                    label: t(l.UnifiedCurrency),
                    isLoading: true,
                    renderer: ({ unifiedCurrency }) =>
                        isNull(unifiedCurrency) ? t(l.PlayerCurrency) : unifiedCurrency, // if from backend the value is null, then label is PlayerCurrency
                },
                reportState: {
                    label: t(l.State),
                    isLoading: true,
                },
                type: {
                    label: t(l.Type),
                    isLoading: true,
                    //TODO change to a single div
                    renderer: ({ type }) => (
                        <div className="crm-object-info-item">
                            {type === ReportTypesLabels.Report ? (
                                <Icon type="bc-icon-crm-standart-report crm-object-type-icon dynamic" />
                            ) : (
                                <Icon type="bc-icon-static crm-object-type-icon static" />
                            )}
                            <p>{t(ReportTypesLabels[type])}</p>
                        </div>
                    ),
                },
                regions: {
                    label: t(l.Regions),
                    isLoading: false,
                },
                categories: {
                    label: t(l.Categories),
                    isLoading: false,
                },
                operation: {
                    label: t(l.Operation),
                    isLoading: true,
                    renderer: ({ operation }) => (
                        <div className="crm-object-info-item">
                            <Icon type={getOperation(operation)?.icon} className="composite-operation-icon" />
                            <p>{t(l[getOperation(operation)?.label ?? ''])}</p>
                        </div>
                    ),
                },
                usedIn: {
                    label: t(l.UsedIn),
                    isLoading: true,
                    renderer: (data) => (
                        <div className="used-in-section">
                            <UsedInSection
                                isLoadingUsedIn={false}
                                usedInData={data}
                                isVisible={true}
                                notUsedInDisplayNameKey={l.NotUsed}
                            />
                        </div>
                    ),
                },
                customerJourneyName: {
                    label: t(l.CustomerJourney),
                    isLoading: true,
                },
                blockName: {
                    label: t(l.CustomerJourneyBlock),
                    isLoading: true,
                },
            },
            isVisible: true,
            key: 'generalDetails',
        },
        {
            title: t(l.Schedule),
            keyValueItemsData: {
                startDate: {
                    label: l.StartDate,
                    isLoading: true,
                    format: (Schedule) => {
                        return isEmpty(Schedule?.CronTime)
                            ? customMomentWithoutTimezoneConversion(Schedule?.StartDate).format(
                                  partnerDateTimeWithoutSecondsFormat,
                              )
                            : customMomentWithoutTimezoneConversion(Schedule?.StartDate).format(partnerDateFormat);
                    },
                },
                endDate: {
                    label: l.EndDate,
                    isLoading: true,
                    format: (Schedule) => {
                        return isEmpty(Schedule?.CronTime)
                            ? customMomentWithoutTimezoneConversion(Schedule?.EndDate).format(
                                  partnerDateTimeWithoutSecondsFormat,
                              )
                            : customMomentWithoutTimezoneConversion(Schedule?.EndDate).format(partnerDateFormat);
                    },
                },
                startTime: {
                    label: l.ExecutionTime,
                    isLoading: true,
                },
                recurrenceCount: {
                    label: l.RecurrenceCount,
                    isLoading: true,
                    renderer: (recurrenceData) => {
                        return (
                            <SkeletonLoader isBusy={isNil(recurrenceData?.UpcomingExecutions)}>
                                <Popover
                                    className="next-run-date-popover-container"
                                    extendTargetWidth={false}
                                    Content={<RecurrenceList upcomingExecutions={recurrenceData?.UpcomingExecutions} />}
                                    align="end"
                                    position="right"
                                >
                                    <button disabled={recurrenceData?.Count === 0} className="blue-color-button">
                                        {recurrenceData?.Count}
                                    </button>
                                </Popover>
                            </SkeletonLoader>
                        );
                    },
                },
                scheduleType: {
                    label: l.ScheduleType,
                    isLoading: true,
                },
                timeZone: {
                    label: l.TimeZone,
                    isLoading: true,
                },
            },
            isVisible: false,
            key: 'schedule',
        },
    ];
};

export { DefaultDatesDict, generalDetailsGroupData };
